/**
 * V13.16 — Hub Fédération (/mes-federations)
 *
 * Point d'entrée central pour les gestionnaires de fédération.
 *
 * États :
 *   - loading : skeleton
 *   - 0 fédé : empty state + CTA "Revendiquer une fédération"
 *   - 1 fédé : carte principale + KPI + compteurs + bouton "Ouvrir mon cockpit"
 *   - N fédés : vue consolidée (KPI agrégés + compteurs totaux) + grille de cartes
 *
 * Réutilise strictement les composants Shadcn et respecte la palette AITO.
 * Auth : `is_user_federation_manager` centralise la résolution côté backend
 * via `get_user_managed_federation_ids` (owner_user_id + EntityManager approved).
 *
 * Droits (rappel — carte "Habilitations") :
 *   ✅ La fédé peut : gérer sa fiche, affiliations, contenus/articles de son
 *      périmètre, mémoires/livrets, import documentaire, reporting, propositions.
 *   ❌ Réservé COPF/Admin : validation institutionnelle finale, fusion doublons,
 *      validation revendication athlète, sélection officielle JP2027,
 *      création canonique globale, paramètres site.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Crown, ShieldCheck, ShieldAlert, Users, Building2, Trophy, Medal, Flag,
  Newspaper, BookOpen, RefreshCw, ArrowRight, ExternalLink, ChevronDown,
  ChevronUp, X, CheckCircle2, XCircle, AlertTriangle, BarChart3, HandCoins,
  MailQuestion, Sparkles,
} from "lucide-react";

const GUIDE_DISMISS_KEY = "aito_fed_hub_guide_dismissed_v1";

const AGG_KPI_ITEMS = [
  { key: "clubs_count", label: "Clubs affiliés", Icon: Building2, tone: "lagoon" },
  { key: "athletes_count", label: "Athlètes du périmètre", Icon: Users, tone: "emerald" },
  { key: "coaches_count", label: "Coachs", Icon: ShieldCheck, tone: "slate" },
  { key: "achievements_total_count", label: "Résultats cumulés", Icon: Trophy, tone: "gold" },
  { key: "gold_count", label: "Or", Icon: Medal, tone: "gold" },
  { key: "silver_count", label: "Argent", Icon: Medal, tone: "slate" },
  { key: "bronze_count", label: "Bronze", Icon: Medal, tone: "slate" },
  { key: "jp2027_selected_count", label: "JP 2027 SELECTED", Icon: Flag, tone: "gold" },
];

function toneClass(tone) {
  return {
    gold: "bg-[hsl(var(--aito-gold))]/10 border-[hsl(var(--aito-gold))]/30 text-[hsl(var(--aito-gold))]",
    lagoon: "bg-[hsl(var(--aito-lagoon))]/10 border-[hsl(var(--aito-lagoon))]/30 text-[hsl(var(--aito-lagoon))]",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    energy: "bg-[hsl(var(--aito-energy))]/10 border-[hsl(var(--aito-energy))]/30 text-[hsl(var(--aito-energy))]",
    neutral: "bg-background",
  }[tone] || "bg-background";
}

function AggKpiTile({ icon: Icon, label, value, tone }) {
  return (
    <Card className={`p-3 flex items-start gap-2 ${toneClass(tone)}`}>
      {Icon && <Icon className="h-4 w-4 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <div className="text-2xl font-bold font-display leading-tight">{value ?? 0}</div>
        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
      </div>
    </Card>
  );
}

function CounterPill({ label, value, tone = "lagoon", to, testid }) {
  const active = (value || 0) > 0;
  const content = (
    <div
      className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition ${
        active
          ? `${toneClass(tone)} border-current`
          : "bg-muted/40 text-muted-foreground border-transparent"
      }`}
      data-testid={testid}
    >
      <span className="font-bold text-sm tabular-nums">{value ?? 0}</span>
      <span className="truncate">{label}</span>
    </div>
  );
  if (!to || !active) return content;
  return <Link to={to} className="focus-ring rounded-md">{content}</Link>;
}

function FederationCard({ entry, isSingle }) {
  const { federation: fed, kpis, counters } = entry;
  const cockpitHref = `/mes-federations/${fed.slug || fed.id}`;
  const publicHref = `/federations/${fed.slug || fed.id}`;
  return (
    <Card
      className={`overflow-hidden ${isSingle ? "md:col-span-2" : ""}`}
      data-testid={`fed-hub-card-${fed.slug || fed.id}`}
    >
      <div className="flex items-start gap-4 p-4 md:p-5 border-b bg-muted/20">
        <div className="h-16 w-16 rounded-xl bg-background flex items-center justify-center overflow-hidden shrink-0 border">
          {fed.logo_url ? (
            <img src={fed.logo_url} alt={fed.name} className="object-cover w-full h-full"
              onError={(e) => { e.currentTarget.src = "/branding/cover-federation-default.png"; }} />
          ) : <Crown className="h-8 w-8 text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Fédération</div>
          <div className="font-display text-lg md:text-xl font-bold truncate" title={fed.name}>
            {fed.name}
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {fed.discipline && <Badge variant="outline" className="capitalize text-[10px]">{fed.discipline}</Badge>}
            {fed.is_copf && <Badge className="bg-[hsl(var(--aito-gold))] text-black text-[10px]">COPF</Badge>}
          </div>
          {fed.short_motto && (
            <div className="text-xs italic text-muted-foreground mt-1 line-clamp-1">« {fed.short_motto} »</div>
          )}
        </div>
      </div>

      <div className="p-4 md:p-5 space-y-3">
        {/* Mini KPI grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <AggKpiTile icon={Users} label="Athlètes" value={kpis?.athletes_count} tone="emerald" />
          <AggKpiTile icon={Building2} label="Clubs" value={kpis?.clubs_count} tone="lagoon" />
          <AggKpiTile icon={Medal} label="Médailles" value={(kpis?.gold_count || 0) + (kpis?.silver_count || 0) + (kpis?.bronze_count || 0)} tone="gold" />
          <AggKpiTile icon={Flag} label="JP 2027" value={kpis?.jp2027_selected_count} tone="gold" />
        </div>

        {/* Compteurs actionnables */}
        <div className="flex flex-wrap gap-1.5">
          <CounterPill
            label="palmarès à valider"
            value={counters?.palmares_to_validate}
            tone="energy"
            to="/federation/validations"
            testid={`fed-hub-counter-palmares-${fed.slug || fed.id}`}
          />
          <CounterPill
            label="affiliations en attente"
            value={counters?.affiliations_pending}
            tone="lagoon"
            to="/mes-validations"
            testid={`fed-hub-counter-affiliations-${fed.slug || fed.id}`}
          />
          <CounterPill
            label="livret(s) actif(s)"
            value={counters?.memory_books_active}
            tone="gold"
            to="/mes-memoires"
            testid={`fed-hub-counter-memory-${fed.slug || fed.id}`}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Link to={cockpitHref} className="flex-1 min-w-[180px]">
            <Button
              className="w-full bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
              data-testid={`fed-hub-open-cockpit-${fed.slug || fed.id}`}
            >
              Ouvrir mon cockpit <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
          <Link to={publicHref}>
            <Button variant="outline" data-testid={`fed-hub-public-${fed.slug || fed.id}`}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Fiche publique
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

function PermissionsGuide({ dismissed, onDismiss, open, onToggle }) {
  if (dismissed) return null;
  return (
    <Card className="p-4 border-l-4 border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/5" data-testid="fed-hub-guide">
      <div className="flex items-center gap-2">
        <Crown className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
        <div className="font-display font-semibold text-sm flex-1">
          Habilitations Fédération · ce que tu peux faire depuis ce hub
        </div>
        <Button size="sm" variant="ghost" onClick={onToggle} data-testid="fed-hub-guide-toggle">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss} data-testid="fed-hub-guide-dismiss" aria-label="Masquer">
          <X className="h-4 w-4" />
        </Button>
      </div>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div className="rounded border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="flex items-center gap-1.5 mb-2 text-emerald-700 font-semibold text-sm">
              <CheckCircle2 className="h-4 w-4" /> Tu peux
            </div>
            <ul className="text-xs space-y-1.5 text-emerald-900">
              <li>• Consulter <strong>athlètes, clubs, coachs</strong> et résultats de ton périmètre</li>
              <li>• Valider les <strong>palmarès</strong> au nom de ta fédération (sceau Fédération)</li>
              <li>• Gérer les <strong>affiliations</strong> (accepter/refuser/révoquer)</li>
              <li>• Publier des <strong>contenus institutionnels</strong> de ton périmètre (communiqués, résultats, etc.)</li>
              <li>• Créer et gérer les <strong>livrets & mémoires</strong> de ton périmètre</li>
              <li>• <strong>Importer des documents</strong> dans le corpus mémoire (sans créer d'entité canonique)</li>
              <li>• Consulter le <strong>reporting palmarès</strong> filtré à ton périmètre</li>
              <li>• <strong>Signaler ou proposer</strong> une correction (doublon, revendication, palmarès, JP2027)</li>
            </ul>
          </div>
          <div className="rounded border border-amber-200 bg-amber-50/50 p-3">
            <div className="flex items-center gap-1.5 mb-2 text-amber-700 font-semibold text-sm">
              <XCircle className="h-4 w-4" /> Réservé COPF / Admin
            </div>
            <ul className="text-xs space-y-1.5 text-amber-900">
              <li>• <strong>Validation institutionnelle finale</strong> (sceau COPF)</li>
              <li>• <strong>Création canonique globale</strong> d'athlètes / clubs / résultats</li>
              <li>• <strong>Fusion de doublons</strong> athlète</li>
              <li>• <strong>Validation des revendications</strong> athlète</li>
              <li>• <strong>Sélection officielle JP 2027</strong> (lecture seule pour la fédé)</li>
              <li>• <strong>Paramètres du site</strong> · gestion des rôles COPF/Admin</li>
              <li>• Administration des <strong>autres fédérations</strong></li>
            </ul>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function FederationHubPage() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [guideOpen, setGuideOpen] = useState(true);
  const [guideDismissed, setGuideDismissed] = useState(false);

  useEffect(() => {
    try { setGuideDismissed(localStorage.getItem(GUIDE_DISMISS_KEY) === "1"); } catch {}
  }, []);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get("/federation-cockpit/my/overview");
      setData(d);
    } catch (e) {
      showError(e, { prefix: "Hub Fédération :" });
      setData(null);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (!authLoading) load(); }, [authLoading, load]);

  const dismissGuide = () => {
    setGuideDismissed(true);
    try { localStorage.setItem(GUIDE_DISMISS_KEY, "1"); } catch {}
  };

  if (authLoading) {
    return (
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4" data-testid="fed-hub-loading">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-lg mx-auto p-6" data-testid="fed-hub-need-login">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-[hsl(var(--aito-energy))] mb-2" />
          <h1 className="text-lg font-semibold">Connexion requise</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ce hub est réservé aux gestionnaires de fédération.
          </p>
          <Link to="/connexion?next=/mes-federations"><Button className="mt-4">Se connecter</Button></Link>
        </Card>
      </div>
    );
  }

  const feds = data?.managed_federations || [];
  const isEmpty = !loading && feds.length === 0;
  const isSingle = feds.length === 1;
  const isMulti = feds.length > 1;
  const agg = data?.aggregated_kpis;
  const ct = data?.counters_total || {};

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4" data-testid="federation-hub-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold flex items-center gap-2">
            <Crown className="h-7 w-7 text-[hsl(var(--aito-gold))]" />
            Mes fédérations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isEmpty
              ? "Tu ne gères pas encore de fédération sur AITO.PF."
              : isSingle
                ? `Ton espace de pilotage — ${feds[0].federation.name}.`
                : `Ton espace de pilotage consolidé (${feds.length} fédérations).`}
          </p>
        </div>
        {!isEmpty && (
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="fed-hub-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Rafraîchir
          </Button>
        )}
      </div>

      {/* Guide habilitations */}
      <PermissionsGuide
        dismissed={guideDismissed}
        onDismiss={dismissGuide}
        open={guideOpen}
        onToggle={() => setGuideOpen((v) => !v)}
      />

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : isEmpty ? (
        <Card className="p-8 text-center" data-testid="fed-hub-empty">
          <Sparkles className="h-10 w-10 mx-auto text-[hsl(var(--aito-lagoon))] mb-2" />
          <h2 className="font-display text-lg font-semibold">Aucune fédération à ton nom</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Pour piloter une fédération, tu dois être <strong>propriétaire</strong> de sa fiche ou avoir été
            approuvé·e comme <strong>gestionnaire</strong> par l'équipe AITO / COPF.
          </p>
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            <Link to="/federations">
              <Button variant="outline" data-testid="fed-hub-browse-feds">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Parcourir les fédérations
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="fed-hub-claim">
                <MailQuestion className="h-3.5 w-3.5 mr-1.5" />Revendiquer une fédération
              </Button>
            </Link>
          </div>
        </Card>
      ) : (
        <>
          {/* Vue consolidée (uniquement si N > 1) */}
          {isMulti && agg && (
            <Card className="p-4 md:p-5 space-y-3" data-testid="fed-hub-aggregate">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                <h2 className="font-display font-semibold text-sm uppercase tracking-wide">
                  Vue consolidée · {feds.length} fédérations
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                {AGG_KPI_ITEMS.map((it) => (
                  <AggKpiTile key={it.key} icon={it.Icon} label={it.label} value={agg[it.key]} tone={it.tone} />
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                <CounterPill
                  label="palmarès à valider"
                  value={ct.palmares_to_validate}
                  tone="energy"
                  to="/federation/validations"
                  testid="fed-hub-agg-counter-palmares"
                />
                <CounterPill
                  label="affiliations en attente"
                  value={ct.affiliations_pending}
                  tone="lagoon"
                  to="/mes-validations"
                  testid="fed-hub-agg-counter-affiliations"
                />
                <CounterPill
                  label="contenus à traiter"
                  value={ct.contents_todo}
                  tone="lagoon"
                  testid="fed-hub-agg-counter-contents"
                />
                <CounterPill
                  label="livrets actifs"
                  value={ct.memory_books_active}
                  tone="gold"
                  to="/mes-memoires"
                  testid="fed-hub-agg-counter-memory"
                />
                <CounterPill
                  label="JP2027 SELECTED"
                  value={ct.jp2027_selected}
                  tone="gold"
                  testid="fed-hub-agg-counter-jp2027"
                />
                {ct.signals_open > 0 && (
                  <CounterPill
                    label="signalements en cours"
                    value={ct.signals_open}
                    tone="energy"
                    testid="fed-hub-agg-counter-signals"
                  />
                )}
              </div>
            </Card>
          )}

          {/* Grille de cartes fédérations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fed-hub-grid">
            {feds.map((entry) => (
              <FederationCard key={entry.federation.id} entry={entry} isSingle={isSingle} />
            ))}
          </div>

          {/* Raccourcis transverses */}
          <Card className="p-4 bg-secondary/40" data-testid="fed-hub-shortcuts">
            <h3 className="font-display font-semibold text-sm mb-2">Raccourcis</h3>
            <div className="flex flex-wrap gap-2 text-xs">
              <Link to="/federation/validations">
                <Button size="sm" variant="outline" data-testid="fed-hub-sc-validations">
                  <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Validations palmarès
                </Button>
              </Link>
              <Link to="/mes-validations">
                <Button size="sm" variant="outline" data-testid="fed-hub-sc-affiliations">
                  <Users className="h-3.5 w-3.5 mr-1.5" />Affiliations
                </Button>
              </Link>
              <Link to="/mes-memoires">
                <Button size="sm" variant="outline" data-testid="fed-hub-sc-memoires">
                  <BookOpen className="h-3.5 w-3.5 mr-1.5" />Mémoires & livrets
                </Button>
              </Link>
              <Link to="/mes-articles">
                <Button size="sm" variant="outline" data-testid="fed-hub-sc-articles">
                  <Newspaper className="h-3.5 w-3.5 mr-1.5" />Articles
                </Button>
              </Link>
              <Link to="/soutien">
                <Button size="sm" variant="outline" data-testid="fed-hub-sc-aides">
                  <HandCoins className="h-3.5 w-3.5 mr-1.5" />Aides sportives
                </Button>
              </Link>
              <Link to="/road-2027">
                <Button size="sm" variant="outline" data-testid="fed-hub-sc-jp2027">
                  <Flag className="h-3.5 w-3.5 mr-1.5" />Route JP 2027
                </Button>
              </Link>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
